import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    componentAutoReaction,
    componentReaction,
    Enums,
    IMultiSelectOption,
    inject,
    TableHeadOptions,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { AccountStore } from '../Account/AccountStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { cellIdToString, IDisplayCellMeta } from '../Displays/DisplayCells';
import { displayTelemetryCells } from '../Displays/DisplayCells_Telemetry';
import { displayAggregateCells } from '../Displays/DisplayCells_Aggregate';
import { displayConfigCells } from './DisplayCells_Config';
import { DisplayTableHeader } from './DisplayTableHeader';

export interface IDisplayHeaderProps {
    renderBefore?(): React.ReactNode;
    renderAfter?(): React.ReactNode;
    readonly vm: DisplayListViewModel;
    readonly blacklist?: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
    readonly whitelist?: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
    readonly className?: string;
    readonly aggregateView?: boolean;
}

@autoinject
@observer
export class DisplayHeader extends React.Component<IDisplayHeaderProps> {
    public render() {
        const { className, renderBefore, renderAfter, vm } = this.props;

        return (
            <thead className={className + ' bg-color'}>
                <tr ref={this.bindHeaderRowRef}>
                    {renderBefore && renderBefore()}

                    {!this.whitelistedCells &&
                        this.displayCells.map(c => (
                            <DisplayTableHeader key={c.id} cell={c} vm={vm} rowRef={this.headerRowRef} />
                        ))}

                    {this.whitelistedCells &&
                        this.whitelistedCells.map(c => (
                            <DisplayTableHeader key={c.id} cell={c} vm={vm} rowRef={this.headerRowRef} />
                        ))}

                    {renderAfter && renderAfter()}
                </tr>
            </thead>
        );
    }

    @bind
    @action
    private bindHeaderRowRef(ref: HTMLTableRowElement | null) {
        this.headerRowRef = ref;
        this.props.vm.headerRowRef = ref;
    }

    @computed
    private get displayCells() {
        const { vm } = this.props;
        const { stickyColumns } = vm;

        const selectedColumns = vm.columns;
        const selectedConfigColumns = vm.configColumns;

        const cells: IDisplayCellMeta[] = [];
        const configCells: IDisplayCellMeta[] = [];

        for (const column of selectedColumns) {
            const cell = displayTelemetryCells.find(c => c.id == column.option);
            if (cell) {
                const index = displayTelemetryCells.indexOf(cell);
                cells.splice(index, 0, cell);
            }
        }

        for (const configColumn of selectedConfigColumns) {
            const configCell = displayConfigCells.find(c => c.id == configColumn.option);
            if (configCell) {
                const index = displayTelemetryCells.indexOf(configCell);
                configCells.splice(index, 0, configCell);
            }
        }

        const joinedCells = cells.concat(configCells);

        joinedCells.sort((a, b) => {
            return stickyColumns.indexOf(cellIdToString(b.id)) - stickyColumns.indexOf(cellIdToString(a.id));
        });

        return this.props.aggregateView ? displayAggregateCells : joinedCells;
    }

    @computed
    private get whitelistedCells() {
        if (this.props.whitelist) {
            const cells: IDisplayCellMeta[] = [];

            for (const column of this.props.whitelist) {
                const cell = displayTelemetryCells.find(c => c.id == column.option);
                if (cell) cells.push(cell);
            }

            return cells;
        }
    }

    @observable
    private headerRowRef: HTMLTableRowElement | null = null;
}
